import { Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Urls } from '@/apiConnection/urls';
import { OfflineCacheService, SELECTORS_PREFETCH_KEY } from './offline-cache.service';
import { environment } from '../../environments/environment';

/**
 * Caches every GET response (and the `selectors` bulk-dropdown POST, which is semantically a
 * read despite the verb) in IndexedDB, and serves from that cache when the network is
 * unreachable. This is the only layer that also covers `<sau-table>`'s own HttpClient calls,
 * since it never goes through ApiService. All other write requests pass through unchanged -
 * offline handling for writes lives in ApiService/OfflineQueueService instead.
 */
@Injectable()
export class OfflineDataInterceptor implements HttpInterceptor {

    constructor(private cache: OfflineCacheService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // The Angular service worker routes every fetch through its `safeFetch`, which turns a
        // network failure into a synthetic `504 Gateway Timeout` instead of letting it surface as
        // `status 0` - and this whole offline layer (this interceptor, ApiService's queueing
        // fallback, ConnectivityService, SyncService) keys off `status === 0`. The API is never
        // cached by the SW (no dataGroups in ngsw-config.json), so bypassing it costs nothing.
        if (request.url.startsWith(environment.apiUrlBase) && !request.headers.has('ngsw-bypass')) {
            request = request.clone({ setHeaders: { 'ngsw-bypass': 'true' } });
        }

        const isSelectors = request.method === 'POST' && request.url === Urls.urls.selectors;

        if (request.method !== 'GET' && !isSelectors) {
            return next.handle(request);
        }

        if (isSelectors) {
            return this.handleSelectors(request, next);
        }
        return this.handleGet(request, next);
    }

    private handleGet(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const key = this.cache.buildKey(request.urlWithParams);

        if (!navigator.onLine) {
            return from(this.cache.get(key)).pipe(
                switchMap(cached => cached !== undefined
                    ? of(new HttpResponse({ body: cached, status: 200, url: request.url }))
                    : next.handle(request))
            );
        }

        return next.handle(request).pipe(
            tap(event => {
                if (event instanceof HttpResponse) {
                    this.cache.put(key, request.url, event.body, 'list');
                }
            }),
            catchError((err: HttpErrorResponse) => {
                if (err.status === 0) {
                    return from(this.cache.get(key)).pipe(
                        switchMap(cached => cached !== undefined
                            ? of(new HttpResponse({ body: cached, status: 200, url: request.url }))
                            : throwError(() => err))
                    );
                }
                return throwError(() => err);
            })
        );
    }

    /** Selectors is a POST, and offline it's served by filtering the upfront prefetch superset down to whatever keys this particular caller asked for. */
    private handleSelectors(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!navigator.onLine) {
            return from(this.filterPrefetch(request.body)).pipe(
                switchMap(filtered => filtered
                    ? of(new HttpResponse({ body: filtered, status: 200, url: request.url }))
                    : next.handle(request))
            );
        }

        return next.handle(request).pipe(
            catchError((err: HttpErrorResponse) => {
                if (err.status === 0) {
                    return from(this.filterPrefetch(request.body)).pipe(
                        switchMap(filtered => filtered
                            ? of(new HttpResponse({ body: filtered, status: 200, url: request.url }))
                            : throwError(() => err))
                    );
                }
                return throwError(() => err);
            })
        );
    }

    private async filterPrefetch(requestBody: any): Promise<any | null> {
        const prefetch = await this.cache.get(SELECTORS_PREFETCH_KEY);
        if (!prefetch) return null;
        const requestedKeys = Object.keys(requestBody || {});
        const body: any = {};
        for (const key of requestedKeys) {
            body[key] = prefetch[key] ?? [];
        }
        return body;
    }
}
